import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Send, User, Bot } from "lucide-react";

const SUGGESTIONS = [
  "Quantas ocorrências existem por unidade?",
  "Quais áreas possuem mais problemas?",
  "Qual cliente possui maior SLA?",
  "Gere um relatório técnico das ocorrências críticas",
  "Gere um plano de ação para as pendências abertas",
];

export default function AIChat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId] = useState(() => {
    const saved = localStorage.getItem("pci-ai-session");
    if (saved) return saved;
    const id = crypto.randomUUID();
    localStorage.setItem("pci-ai-session", id);
    return id;
  });
  const bottomRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/ai/messages?session_id=${sessionId}`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => Array.isArray(data) && setMessages(data.map((m) => ({ role: m.role, content: m.content }))));
  }, [sessionId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (text) => {
    const message = (text || input).trim();
    if (!message || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message, session_id: sessionId }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const obj = JSON.parse(payload);
            if (obj.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + obj.delta };
                return copy;
              });
            }
            if (obj.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: `Erro: ${obj.error}` };
                return copy;
              });
            }
          } catch (e) { /* partial json */ }
        }
      }
    } catch (err) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Erro de conexão com o ASTRAL AI. Tente novamente." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto flex flex-col h-[calc(100vh-8rem)]" data-testid="ai-chat-page">
      <div className="flex items-center gap-3 pb-4 border-b border-border">
        <div className="h-10 w-10 rounded-lg bg-primary/15 flex items-center justify-center">
          <Sparkles size={19} className="text-primary" />
        </div>
        <div>
          <h1 className="font-heading text-xl font-bold tracking-tight">ASTRAL AI</h1>
          <p className="text-xs text-muted-foreground">ASTRAL Saúde Ambiental — análises, relatórios técnicos e planos de ação com dados reais.</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin py-6 space-y-5">
        {messages.length === 0 && (
          <div className="space-y-3 pt-8">
            <p className="text-sm text-muted-foreground text-center">Experimente perguntar:</p>
            <div className="flex flex-wrap justify-center gap-2">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)}
                  className="text-xs px-3.5 py-2 rounded-full border border-border bg-card hover:border-primary/60 hover:text-primary transition-colors duration-150"
                  data-testid="ai-suggestion-chip">{s}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`} data-testid={`ai-message-${m.role}`}>
            {m.role === "assistant" && (
              <div className="h-8 w-8 rounded-md bg-primary/15 flex items-center justify-center shrink-0 mt-1">
                <Bot size={15} className="text-primary" />
              </div>
            )}
            <div className={`max-w-[85%] rounded-xl px-4 py-3 text-sm ${
              m.role === "user" ? "bg-primary text-white" : "bg-card border border-border"
            }`}>
              {m.role === "assistant" ? (
                m.content ? <div className="ai-markdown"><ReactMarkdown>{m.content}</ReactMarkdown></div>
                  : <span className="inline-flex gap-1 items-center text-muted-foreground">
                      <span className="h-1.5 w-1.5 rounded-full bg-primary animate-bounce" />
                      <span className="h-1.5 w-1.5 rounded-full bg-primary animate-bounce [animation-delay:120ms]" />
                      <span className="h-1.5 w-1.5 rounded-full bg-primary animate-bounce [animation-delay:240ms]" />
                    </span>
              ) : m.content}
            </div>
            {m.role === "user" && (
              <div className="h-8 w-8 rounded-md bg-muted flex items-center justify-center shrink-0 mt-1">
                <User size={15} className="text-muted-foreground" />
              </div>
            )}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <div className="flex gap-2 pt-4 border-t border-border">
        <Textarea rows={1} value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Pergunte sobre suas ocorrências, SLA, áreas críticas..."
          className="resize-none min-h-[44px]" data-testid="ai-input" />
        <Button onClick={() => send()} disabled={streaming || !input.trim()} size="icon" className="h-11 w-11 shrink-0" data-testid="ai-send-btn">
          <Send size={16} />
        </Button>
      </div>
    </div>
  );
}
